import { useIntl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { useClient } from '../ClientProvider';
import ErrCode from '../ErrCode';
import { BillingStatus } from '../enums';
import useMutation from '../useMutation';
import useQuery from '../useQuery';
export const COUNTRIES_WITH_SUBSIDIARIES = ['FR', 'ES', 'DE'];
/** Use a plan/billing feature information */
export const usePlanFeat = (featName, billingStatus) => {
    const client = useClient();
    const featsQuery = useQuery(client.billing.getPlanFeaturesByBilling());
    if (isNil(billingStatus))
        return undefined;
    const featuresByBillingStatus = featsQuery.data?.[BillingStatus[billingStatus]];
    return featuresByBillingStatus ? featuresByBillingStatus?.[featName] : undefined;
};
export const newPlansMonthlyPrice = {
    essential: 0,
    plus: 14,
    expert: 25
};
const defaultIfNil = (value) => (isNil(value) ? '' : value);
export const useBillingAccount = ({ fetchBillingAccount = true, organizationId }) => {
    const client = useClient();
    const getBillingAccountRequest = useQuery(client.billing.getAccount(organizationId), {
        enabled: !isNil(organizationId) && fetchBillingAccount
    });
    const billingAccount = getBillingAccountRequest.data;
    // Otherwise form inputs are not happy
    const billingAccountWithoutNulls = isNil(billingAccount)
        ? undefined
        : {
            city: defaultIfNil(billingAccount.city),
            country: isNil(billingAccount.country) ? 'FR' : billingAccount.country,
            addressLine1: defaultIfNil(billingAccount.addressLine1),
            addressLine2: defaultIfNil(billingAccount.addressLine2),
            zipCode: defaultIfNil(billingAccount.zipCode),
            organizationName: defaultIfNil(billingAccount.organizationName),
            siret: defaultIfNil(billingAccount.siret)
        };
    return { billingAccount: billingAccountWithoutNulls, getBillingAccountRequest };
};
export const useHasPaymentIncident = ({ enabled, orgId }) => {
    const client = useClient();
    const paymentIncidentRequest = useQuery(client.billing.getPaymentIncident(orgId), {
        enabled
    });
    const dueDate = paymentIncidentRequest.data?.paymentDueDate;
    const hasPaymentIncident = enabled && !isNil(dueDate) && moment().unix() - dueDate > 24 * 60 * 60;
    return { hasPaymentIncident, paymentIncidentRequest };
};
export const useBillingAccountEdition = ({ organizationId, onSuccess, onError, onFieldErrors }) => {
    const client = useClient();
    const { t } = useIntl();
    const { billingAccount, getBillingAccountRequest } = useBillingAccount({
        organizationId
    });
    const subPricesRequester = client.billing.getSubscriptionPrices(organizationId);
    const [editBillingAccount, editBillingAccountRequest] = useMutation(client.billing.editAccount(organizationId), {
        onSuccess,
        onError: (e) => {
            onFieldErrors?.(e);
            const hasMissingOrInvalidSiret = !isNil(e?.nonFieldErrors?.find((e) => e.code === ErrCode.orgMissingSiret));
            const hasMissingOrInvalidVatNumber = !isNil(e?.nonFieldErrors?.find((e) => e.code === ErrCode.missingOrInvalidVatNumber));
            const hasSiretAlreadyUsed = !isNil(e?.nonFieldErrors?.find((e) => e.code === ErrCode.siretAlreadyUsed));
            if (hasMissingOrInvalidSiret || hasSiretAlreadyUsed || hasMissingOrInvalidVatNumber) {
                let code = null;
                if (hasSiretAlreadyUsed) {
                    code = ErrCode.siretAlreadyUsed;
                }
                else if (hasMissingOrInvalidSiret) {
                    code = ErrCode.orgMissingSiret;
                }
                else if (hasMissingOrInvalidVatNumber) {
                    code = ErrCode.missingOrInvalidVatNumber;
                }
                if (!isNil(code)) {
                    onError(t(`error_codes.${code}`));
                }
            }
        },
        keysToInvalidate: [subPricesRequester.invalidationKey]
    });
    return {
        billingAccount,
        getBillingAccountRequest,
        editBillingAccount,
        editBillingAccountRequest
    };
};
export const useRestrictedPlan = (orgId) => {
    const client = useClient();
    const devices = useQuery(client.devices.getAll({ ownedByOrganizationId: orgId }), {
        enabled: !isNil(orgId)
    });
    if (isNil(orgId)) {
        return undefined;
    }
    else if (!isNil(devices.data?.find((device) => device.availableMeasures.includes('HPOT') || device.availableMeasures.includes('U_CAPA')))) {
        return 'plus';
    }
};
